import React, { useState, useEffect } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { app } from "../../firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen } from "lucide-react";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  // Dynamic URL handler for different environments
  const getEnvironmentURL = () => {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    // Development environments
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${origin}/authentication`;
    }

    // Production environments - using known hosting platforms
    const productionPlatforms = [
      "vercel.app",
      "netlify.app",
      "herokuapp.com",
      "onrender.com",
      "railway.app",
      "fly.dev",
      "pages.dev",
      "firebaseapp.com",
      "web.app",
      "github.io",
      "gitlab.io",
      "surge.sh",
    ];

    if (productionPlatforms.some((platform) => hostname.includes(platform))) {
      return `${origin}/authentication`;
    }

    // Custom domain or other environments
    return `${origin}/authentication`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setIsLoading(true);

    const auth = getAuth(app);

    try {
      // Method 1: Try with continue URL first
      const continueUrl = getEnvironmentURL();

      await sendPasswordResetEmail(auth, email, {
        url: continueUrl,
        handleCodeInApp: false, // Important: set to false for email links
      });

      setMessage(
        "Password reset email has been sent! Please check your inbox and spam folder. The link will expire in 1 hour."
      );
      setShowModal(true);

      setTimeout(() => {
        setShowModal(false);
        setMessage("");
        navigate("/authentication");
      }, 5000);
    } catch (err) {
      console.error("Password reset error:", err.code, err.message);
      console.error("Current domain:", window.location.hostname);
      console.error("Continue URL attempted:", getEnvironmentURL());

      // If continue URL fails, try without it
      if (
        err.code === "auth/unauthorized-continue-uri" ||
        err.code === "auth/invalid-continue-uri" ||
        err.code === "auth/missing-continue-uri"
      ) {
        //console.log("Attempting fallback without continue URL...");

        try {
          // Method 2: Fallback without continue URL
          await sendPasswordResetEmail(auth, email);

          setMessage(
            "Password reset email has been sent! Please check your inbox and spam folder."
          );
          setShowModal(true);

          setTimeout(() => {
            setShowModal(false);
            setMessage("");
            navigate("/authentication");
          }, 5000);

          return; // Exit successfully
        } catch (fallbackErr) {
          console.error("Fallback method also failed:", fallbackErr);
          // Continue to handle the fallback error below
          err = fallbackErr;
        }
      }

      // Handle all other errors
      switch (err.code) {
        case "auth/user-not-found":
          setError(
            "If this email is registered, you will receive a reset link."
          );
          break;
        case "auth/invalid-email":
          setError("Please enter a valid email address.");
          break;
        case "auth/too-many-requests":
          setError(
            "Too many reset attempts. Please wait a few minutes before trying again."
          );
          break;
        case "auth/unauthorized-continue-uri":
          setError(
            "Domain authorization issue. Please add your domain to Firebase Console > Authentication > Settings > Authorized domains"
          );
          console.error(
            "Add this domain to Firebase:",
            window.location.hostname
          );
          break;
        case "auth/missing-continue-uri":
          setError("Configuration error. Using alternative method...");
          break;
        case "auth/invalid-continue-uri":
          setError("Invalid redirect URL configuration.");
          break;
        case "auth/user-disabled":
          setError("This account has been disabled. Please contact support.");
          break;
        case "auth/network-request-failed":
          setError(
            "Network error. Please check your internet connection and try again."
          );
          break;
        default:
          setError(
            "If this email is registered with us, you will receive a password reset link."
          );
          console.error("Unexpected error:", err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/authentication");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setMessage("");
    navigate("/authentication");
  };

  return (
    <div
      style={{
        backgroundColor: "#f6f9fc",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <style>
        {`
          .login-card {
          width: 500px;
          
            border-radius: 12px;
            box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
            background-color: #fff;
            padding: 2.5rem;
          }
          .form-label {
            font-size: 1rem;
            color: #666;
            margin-bottom: 0.75rem;
            display: block;
            font-weight: 500;
          }
          .form-control {
            border: 1px solid #e0e0e0;
            border-radius: 0;
            padding: 0.9rem;
            font-size: 1.1rem;
            color: #333;
            width: 100%;
            box-sizing: border-box;
            transition: all 0.2s ease;
          }
          .form-control:focus {
            border-color: #007bff;
            box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
            outline: none;
          }
          .form-control:disabled {
            background-color: #f8f9fa;
            opacity: 0.6;
            cursor: not-allowed;
          }
          .btn-primary {
            background-color: #007bff;
            border: none;
            border-radius: 6px;
            padding: 0.9rem;
            font-size: 1.1rem;
            font-weight: 500;
            width: 100%;
            color: #fff;
            cursor: pointer;
            transition: background-color 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .btn-primary:hover:not(:disabled) {
            background-color: #0056b3;
          }
          .btn-primary:disabled {
            background-color: #66a3ff;
            cursor: not-allowed;
          }
          .link {
            font-size: 1rem;
            color: #007bff;
            text-decoration: none;
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            transition: color 0.2s ease;
          }
          .link:hover {
            text-decoration: underline;
            color: #0056b3;
          }
          .error-message {
            font-size: 1rem;
            color: #dc3545;
            margin-bottom: 1.25rem;
            padding: 0.75rem;
            background-color: #f8d7da;
            border: 1px solid #f5c6cb;
            border-radius: 6px;
            line-height: 1.4;
          }
          .success-message {
            font-size: 1rem;
            color: #155724;
            margin-bottom: 1.25rem;
            padding: 0.75rem;
            background-color: #d4edda;
            border: 1px solid #c3e6cb;
            border-radius: 6px;
            line-height: 1.4;
          }
          .input-group {
            display: flex;
            align-items: center;
            width: 100%;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
            transition: border-color 0.2s ease;
          }
          .input-group:focus-within {
            border-color: #007bff;
            box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
          }
          .input-group-text {
            background-color: #f9f9f9;
            border: none;
            cursor: pointer;
            padding: 0.9rem;
            display: flex;
            align-items: center;
            color: #666;
          }
          .input-group-text i {
            font-size: 18px;
          }
          .logo-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.75rem;
          }
          .logo-text {
            font-size: 1.4rem;
            font-weight: 700;
            color: #111827;
          }
          .flex-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0.75rem;
          }
          .text-center {
            text-align: center;
            margin-bottom: 1.75rem;
          }
          .mb-2 { margin-bottom: 0.5rem; }
          .mt-2 { margin-top: 0.5rem; }
          .mb-0 { margin-bottom: 0; }
          .mb-4 { margin-bottom: 1rem; }
          .mt-3 { margin-top: 0.75rem; }
          .modal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            animation: fadeIn 0.3s ease;
          }
          .modal-content {
            background-color: #fff;
            padding: 2rem;
            border-radius: 12px;
            max-width: 400px;
            width: 90%;
            text-align: center;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
            animation: slideIn 0.3s ease;
          }
          .modal-message {
            font-size: 1rem;
            color: #28a745;
            margin-bottom: 1.5rem;
            line-height: 1.5;
          }
          .spinner {
            width: 16px;
            height: 16px;
            border: 2px solid #ffffff;
            border-top: 2px solid transparent;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-right: 0.5rem;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { transform: translateY(-20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          /* Mobile Responsiveness */
          @media (max-width: 767px) {
            .login-card {
              max-width: 90%;
              padding: 1.5rem;
            }
            .form-label {
              font-size: 0.9rem;
            }
            .form-control {
              padding: 0.8rem;
              font-size: 1rem;
            }
            .btn-primary {
              padding: 0.8rem;
              font-size: 1rem;
            }
            .link {
              font-size: 0.9rem;
            }
            .error-message, .success-message {
              font-size: 0.9rem;
              margin-bottom: 1rem;
              padding: 0.6rem;
            }
            .input-group-text {
              padding: 0.8rem;
            }
            .input-group-text i {
              font-size: 16px;
            }
            .logo-container {
              margin-right: 0.5rem;
            }
            .logo-text {
              font-size: 1.2rem;
            }
            .text-center {
              margin-bottom: 1.25rem;
            }
            .mb-4 {
              margin-bottom: 0.75rem;
            }
            .mt-3 {
              margin-top: 0.5rem;
            }
            .modal-content {
              padding: 1.5rem;
              max-width: 85%;
            }
            .modal-message {
              font-size: 0.9rem;
            }
          }
        `}
      </style>

      <div className="ForgotPassword">
        <div className="text-center">
          <div className="flex-container">
            <div
              className="logo-container"
              style={{
                width: "2.25rem",
                height: "2.25rem",
                background: "linear-gradient(to right, #2563eb, #10b981)",
                borderRadius: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BookOpen
                style={{ width: "1.35rem", height: "1.35rem", color: "#fff" }}
              />
            </div>
            <span className="logo-text">InternSkool</span>
          </div>
          <h3
            className="mb-2 mt-2"
            style={{ fontWeight: "600", color: "#333", fontSize: "1.75rem" }}
          >
            Forgot Password
          </h3>
          <p className="mb-0" style={{ color: "#666", fontSize: "1.1rem" }}>
            Enter your email to reset your password
          </p>
        </div>

        <div className="login-card">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-envelope-at-fill"></i>
                </span>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                  disabled={isLoading}
                  style={{ border: "none" }}
                />
              </div>
            </div>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {message && !showModal && (
              <div className="success-message">
                <strong>Success:</strong> {message}
              </div>
            )}

            <button
              className="btn-primary"
              type="submit"
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </button>

            <div className="text-center mt-3">
              <button
                type="button"
                className="link"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                ← Back to Sign In
              </button>
            </div>
          </form>
        </div>

        {showModal && (
          <div
            className="modal"
            onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
          >
            <div className="modal-content">
              <div className="modal-message">
                <strong>✅ Success!</strong>
                <br />
                {message}
              </div>
              <button
                className="btn-primary"
                onClick={handleCloseModal}
                style={{ maxWidth: "200px" }}
              >
                Continue to Login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;